// useMediaQuery — subscribe to a CSS media query.
//
// CTSK-522: first shared viewport hook in apps/web (there was no prior
// breakpoint helper). Exported from hooks/ so other surfaces (e.g. the
// CTSK-523 Settings reorg) can reuse it.
//
// `fallback` is what the hook reports where `window.matchMedia` doesn't
// exist (SSR, jsdom). It is also the value on first render in those
// environments. Pick the fallback that keeps the UI in its "normal"
// state — for width queries that's usually `true` (wide viewport).

import { useEffect, useState } from 'react';

function read(query: string, fallback: boolean): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return fallback;
  return window.matchMedia(query).matches;
}

export function useMediaQuery(query: string, fallback = false): boolean {
  const [matches, setMatches] = useState<boolean>(() => read(query, fallback));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
